import { useRef, useState } from "react";
import { compatApi } from "../../utils/Intercepter";
import { toast } from "react-toastify";
import toastConfig from "../../const/toastConfig";
import { useDaumPostcodePopup } from 'react-daum-postcode';
import openApiRequest from "../../utils/OpenApiRequest";
import { PURPLE, MINT, hexToRgba } from "../../utils/designUtils";
import OrgSectionTabs from "../../components/OrgSectionTabs";

export default function OrganizationAdd() {
  const [formData, setFormData] = useState({
    organizationName: '',
    bizRegNo: '',
    isNonBusiness: false,
    address: '',
    addressDetail: ''
  });

  const [orgNameStatus, setOrgNameStatus] = useState({
    isChecking: false,
    isValid: false,
    message: ''
  });

  const [agree, setAgree] = useState(false);
  const orgNameTimerRef = useRef(null);
  const popupOpen = useDaumPostcodePopup();

  const checkOrgNameDuplicate = async (orgName) => {
    if (!orgName.trim()) { setOrgNameStatus({ isChecking: false, isValid: false, message: '' }); return; }
    try {
      const response = await compatApi.get(`/org/duplicate-orgName/${encodeURIComponent(orgName)}`);
      if (response.ok) {
        setOrgNameStatus({
          isChecking: false,
          isValid: !response.data,
          message: response.data ? '이미 사용 중인 조직명입니다.' : '사용 가능한 조직명입니다.'
        });
      } else {
        setOrgNameStatus({ isChecking: false, isValid: false, message: '중복 검사에 실패했습니다.' });
      }
    } catch {
      setOrgNameStatus({ isChecking: false, isValid: false, message: '중복 검사 중 오류가 발생했습니다.' });
    }
  };

  const handleOrgNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, organizationName: value }));
    if (orgNameTimerRef.current) clearTimeout(orgNameTimerRef.current);
    if (!value.trim()) { setOrgNameStatus({ isChecking: false, isValid: false, message: '' }); return; }
    setOrgNameStatus({ isChecking: true, isValid: false, message: '확인 중...' });
    orgNameTimerRef.current = setTimeout(() => checkOrgNameDuplicate(value), 500);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNonBusinessCheck = (e) => {
    const isChecked = e.target.checked;
    setFormData(prev => ({ ...prev, isNonBusiness: isChecked, bizRegNo: isChecked ? '' : prev.bizRegNo }));
  };

  const handleBizRegNoChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length <= 3) { /* keep */ }
    else if (value.length <= 5) value = value.slice(0, 3) + "-" + value.slice(3);
    else value = value.slice(0, 3) + "-" + value.slice(3, 5) + "-" + value.slice(5, 10);
    setFormData(prev => ({ ...prev, bizRegNo: value }));
  };

  const handleBizRegNoVerify = async () => {
    const businessNumber = formData.bizRegNo.replace(/-/g, '');
    if (businessNumber.length !== 10) { toast.warn("사업자번호를 정확히 입력해 주세요", toastConfig); return; }
    try {
      const response = await compatApi.get('/org/duplicate-bizRegNo/' + formData.bizRegNo);
      if (response.ok) {
        if (response.data) { toast.error('동일한 사업자번호가 이미 등록되어 있습니다.', toastConfig); return; }
      } else { toast.error('사업자번호 중복 검사에 실패했습니다.', toastConfig); return; }
    } catch { toast.error('중복 검사 중 오류가 발생했습니다.', toastConfig); return; }
    try {
      const statusResponse = await openApiRequest.getStatus({ "b_no": [businessNumber] });
      const statusResult = JSON.parse(await statusResponse.text());
      if (statusResponse.ok && statusResult.data?.[0]) {
        const businessStatus = statusResult.data[0].b_stt;
        if (!businessStatus || businessStatus === "휴업자" || businessStatus === "폐업자") {
          toast.error(`사업자 상태: ${businessStatus || '조회불가'} - 유효하지 않은 사업자입니다.`, toastConfig);
        } else { toast.success("사업자 등록 확인이 완료되었습니다.", toastConfig); }
      } else { toast.error("사업자 정보를 찾을 수 없습니다.", toastConfig); }
    } catch { toast.error("사업자번호 검증 중 오류가 발생했습니다.", toastConfig); }
  };

  const handleAddressComplete = (data) => {
    let fullAddress = data.address;
    let extraAddress = '';
    if (data.addressType === 'R') {
      if (data.bname !== '') extraAddress += data.bname;
      if (data.buildingName !== '') extraAddress += (extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName);
      if (extraAddress !== '') fullAddress += ` (${extraAddress})`;
    }
    setFormData(prev => ({ ...prev, address: fullAddress }));
  };

  const handleSubmit = async () => {
    if (!formData.organizationName.trim()) { toast.warn("조직명을 입력해주세요.", toastConfig); return; }
    if (!orgNameStatus.isValid) { toast.warn("유효한 조직명을 입력해주세요.", toastConfig); return; }
    if (!formData.isNonBusiness && !formData.bizRegNo.trim()) { toast.warn("사업자번호를 입력하거나 비사업자 조직으로 선택해주세요.", toastConfig); return; }
    if (!formData.address.trim()) { toast.warn("주소를 입력해주세요.", toastConfig); return; }
    try {
      const response = await compatApi.post('/org/createOrganization', {
        organizationName: formData.organizationName,
        bizRegNo: formData.isNonBusiness ? null : formData.bizRegNo,
        address: formData.address,
        addressDetail: formData.addressDetail,
        isNonBusiness: formData.isNonBusiness
      });
      if (response.ok) {
        toast.success('조직이 생성되었습니다.', toastConfig);
        setTimeout(() => { window.location.href = '/'; }, 500);
      } else { toast.error('조직 생성에 실패했습니다.', toastConfig); }
    } catch { toast.error('조직 생성 중 오류가 발생했습니다.', toastConfig); }
  };

  const handleCancel = () => {
    setFormData({ organizationName: '', bizRegNo: '', isNonBusiness: false, address: '', addressDetail: '' });
    setOrgNameStatus({ isChecking: false, isValid: false, message: '' });
    setAgree(false);
  };

  const canSubmit = agree && formData.organizationName.trim() && orgNameStatus.isValid;

  const inputStyle = (extra = {}) => ({
    height: 42, borderRadius: 10, padding: '0 14px', width: '100%',
    border: '1.5px solid #ececf2', background: 'white',
    fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit',
    transition: 'border-color 140ms, box-shadow 140ms', boxSizing: 'border-box',
    ...extra,
  });
  const focusOn = (e) => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, .10)}`; };
  const focusOff = (e) => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; };

  const orgNameBorder = orgNameStatus.isValid ? '#22c55e'
    : (orgNameStatus.message && !orgNameStatus.isValid && !orgNameStatus.isChecking) ? '#ef4444'
      : '#ececf2';

  return (
    <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
      <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>
        <OrgSectionTabs active="org-create" />

        <div style={{
          background: 'white', borderRadius: 18,
          border: '1px solid #f0eff5',
          boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 22px',
            background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
            borderBottom: '1px solid #ececf2',
          }}>
            <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>새 조직 만들기</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#8b8a99' }}>
              생성 후 본인이 MASTER 권한을 가지며 자동으로 6자리 조직 코드가 발급됩니다.
            </p>
          </div>

          <div style={{ padding: '24px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* 조직명 */}
            <Field label="조직 이름" required>
              <input type="text" value={formData.organizationName} onChange={handleOrgNameChange}
                placeholder="예) (주) 워키톡코리아" maxLength={100}
                style={inputStyle({ border: `1.5px solid ${orgNameBorder}` })}
                onFocus={focusOn} onBlur={focusOff} />
              {orgNameStatus.message && (
                <span style={{ fontSize: 11.5, marginTop: 2, color: orgNameStatus.isValid ? '#22c55e' : orgNameStatus.isChecking ? '#7a7889' : '#ef4444' }}>
                  {orgNameStatus.message}
                </span>
              )}
              {!orgNameStatus.message && <span style={{ fontSize: 11.5, color: '#8b8a99', marginTop: 2 }}>2~30자 / 한글, 영문, 숫자, 공백 사용 가능</span>}
            </Field>

            {/* 사업자번호 + 업종 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="사업자 등록번호">
                {!formData.isNonBusiness ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="text" value={formData.bizRegNo} onChange={handleBizRegNoChange}
                      placeholder="000-00-00000" maxLength={12}
                      style={inputStyle({ flex: 1 })}
                      onFocus={focusOn} onBlur={focusOff} />
                    <button type="button" onClick={handleBizRegNoVerify} style={{
                      height: 42, padding: '0 14px', borderRadius: 10,
                      background: 'white', border: `1.5px solid ${PURPLE}`,
                      color: PURPLE, fontSize: 12.5, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                    }}>확인</button>
                  </div>
                ) : (
                  <div style={{ height: 42, borderRadius: 10, border: '1.5px solid #ececf2', background: '#f5f4fa', display: 'flex', alignItems: 'center', padding: '0 14px' }}>
                    <span style={{ fontSize: 13, color: '#aaa9b7' }}>비사업자 조직</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6 }}>
                  <span
                    onClick={() => handleNonBusinessCheck({ target: { checked: !formData.isNonBusiness } })}
                    style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      background: formData.isNonBusiness ? MINT : 'white',
                      border: `1.5px solid ${formData.isNonBusiness ? MINT : '#d0cfdb'}`,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', transition: 'all 140ms',
                    }}
                  >
                    {formData.isNonBusiness && (
                      <svg width="10" height="10" viewBox="0 0 12 12">
                        <path d="M2 6.5L5 9.5L10 3.5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span onClick={() => handleNonBusinessCheck({ target: { checked: !formData.isNonBusiness } })}
                    style={{ fontSize: 12, color: '#5d5b6e', cursor: 'pointer', userSelect: 'none' }}>
                    비사업자 조직
                  </span>
                </div>
              </Field>

              <Field label="대표 업종">
                <select style={{
                  ...inputStyle(),
                  appearance: 'none',
                  backgroundImage: "linear-gradient(45deg, transparent 50%, #8b8a99 50%), linear-gradient(135deg, #8b8a99 50%, transparent 50%)",
                  backgroundPosition: 'calc(100% - 18px) 16px, calc(100% - 13px) 16px',
                  backgroundSize: '5px 5px, 5px 5px',
                  backgroundRepeat: 'no-repeat',
                  paddingRight: 32,
                }}>
                  <option>선택하세요</option>
                  <option>IT · 소프트웨어</option>
                  <option>금융 · 보험</option>
                  <option>제조 · 유통</option>
                  <option>교육 · 연구</option>
                  <option>기타</option>
                </select>
              </Field>
            </div>

            {/* 주소 */}
            <Field label="주소">
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" value={formData.address} readOnly
                  placeholder="우편번호 또는 도로명 주소"
                  style={inputStyle({ flex: 1, background: formData.address ? 'white' : '#fafaff' })} />
                <button type="button" onClick={() => popupOpen({ onComplete: handleAddressComplete })} style={{
                  height: 42, padding: '0 16px', borderRadius: 10,
                  background: 'white', border: `1.5px solid ${PURPLE}`,
                  color: PURPLE, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}>주소 검색</button>
              </div>
              {formData.address && (
                <input type="text" name="addressDetail" value={formData.addressDetail}
                  onChange={handleInputChange}
                  placeholder="상세 주소를 입력해 주세요"
                  style={inputStyle({ marginTop: 8 })}
                  onFocus={focusOn} onBlur={focusOff} />
              )}
            </Field>

            {/* 이용약관 */}
            <label
              onClick={() => setAgree(!agree)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 14px', borderRadius: 10,
                background: '#fafaff', border: '1px solid #ececf2',
                cursor: 'pointer', userSelect: 'none',
              }}
            >
              <span style={{
                width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                background: agree ? MINT : 'white',
                border: `1.5px solid ${agree ? MINT : '#d0cfdb'}`,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                marginTop: 1, transition: 'all 140ms',
              }}>
                {agree && (
                  <svg width="11" height="11" viewBox="0 0 12 12">
                    <path d="M2 6.5L5 9.5L10 3.5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span style={{ fontSize: 13, color: '#3a3849', lineHeight: 1.5 }}>
                <b style={{ color: '#1f1d2e' }}>이용약관 및 데이터 처리 방침</b>에 동의합니다.
                <span style={{ display: 'block', fontSize: 11.5, color: '#8b8a99', marginTop: 2 }}>
                  생성된 조직 데이터는 WorkiTalk의 멀티테넌트 정책에 따라 격리 저장됩니다.
                </span>
              </span>
            </label>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 8,
            padding: '16px 22px', borderTop: '1px solid #ececf2',
            background: '#fafaff',
          }}>
            <button onClick={handleCancel} style={{
              height: 40, padding: '0 18px', borderRadius: 10,
              background: 'white', border: '1.5px solid #ececf2',
              color: '#5d5b6e', fontSize: 13, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>취소</button>
            <button disabled={!canSubmit} onClick={handleSubmit} style={{
              height: 40, padding: '0 22px', borderRadius: 10, border: 'none',
              background: !canSubmit ? '#d4d3df' : `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
              color: 'white', fontSize: 13, fontWeight: 600,
              cursor: !canSubmit ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              boxShadow: !canSubmit ? 'none' : `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`,
            }}>조직 생성</button>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: '#3a3849' }}>
        {label}
        {required && <span style={{ color: '#d97757', marginLeft: 4 }}>*</span>}
      </label>
      {children}
    </div>
  );
}
